import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./Details.css";
import { Context } from "../../../context/Context";
import { useRestaurantAccess } from "../../../access/hooks/useRestaurantAccess";
import { formatter, monthNames } from "../../../utils/helpers";
import { DetailsExpand } from "./detailsExpand/DetailsExpand";
import { translations } from "../../../utils/lang";
import { COMP_FIELDS2 } from "../../../utils/Compensation";
import { exportToExcel } from "../../../utils/exportExcel";
import { deleteCompByIdsAPI, fetchCompsAPI } from "../../../utils/api/report";
import { DeleteModal } from "../../modals/Delete";
import {
  ArrowDown,
  ArrowUp,
  Button,
  CloudDownload,
  Input,
  SearchCheckList,
  Trash2,
} from "../../ui";
import { groupBy, pruneSelection, rowToggleProps } from "../reportUtils";
import { getReportText } from "../reportText";
import { FEATURE_NAMES, isFeatureEnabled } from "../../../utils/features";
import { DetailsPaged } from "./DetailsPaged";

const DetailsUnpaged = () => {
  const { lang, storeAccess, handleTokenExpiration } = useContext(Context);
  const { can, canForAll } = useRestaurantAccess();
  const [details, setDetails] = useState([]);
  const [searchText, setSearchText] = useState({ date: "", other: "" });
  const [loading, setLoading] = useState(true);
  const [deleteError, setDeleteError] = useState("");
  const text = getReportText(lang);
  // The context recreates these on every provider render; a ref keeps the
  // fetcher stable so it runs once on mount.
  const contextRef = useRef({ storeAccess, handleTokenExpiration });
  contextRef.current = { storeAccess, handleTokenExpiration };
  const [checkboxFilters, setCheckboxFilters] = useState({});
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const maps = useMemo(
    () => ({
      orderStatus: translations[lang].filter.orderStatus,
      app: translations[lang].filter.apps,
    }),
    [lang],
  );

  const checkboxFields = useMemo(
    () => [
      COMP_FIELDS2.app,
      COMP_FIELDS2.storeName,
      COMP_FIELDS2.branchName,
      COMP_FIELDS2.orderStatus,
      COMP_FIELDS2.cause,
    ],
    [],
  );

  const normalize = (val) =>
    String(val ?? "")
      .trim()
      .toLowerCase();

  const fetchDetails = useCallback(async () => {
    const { storeAccess: access, handleTokenExpiration: expire } =
      contextRef.current;
    try {
      const token = localStorage.getItem("auth-token");
      if (token) {
        const result = await fetchCompsAPI(token, access);
        // Existing behaviour: the helper can't tell an expired session from
        // other failures, and a failed load is treated as expiry.
        if (result) setDetails(result);
        else expire();
      } else {
        expire();
      }
    } catch (error) {
      console.error("Fetching failed details:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const uniqueOptionsMap = useMemo(() => {
    return checkboxFields.reduce((acc, field) => {
      const set = new Set(
        details
          .map((item) => item[field])
          .filter(Boolean)
          .map(normalize),
      );
      acc[field] = Array.from(set);
      return acc;
    }, {});
  }, [details, checkboxFields]);

  const getOptionLabel = (field, val) => {
    if (field === COMP_FIELDS2.app) {
      return (
        maps[field]?.[val] ??
        translations[lang].notApp + " " + val[val.length - 1]
      );
    }

    return maps[field]?.[val] ?? val;
  };

  const filteredBranches = useMemo(() => {
    const selectedStores = checkboxFilters[COMP_FIELDS2.storeName] || [];

    if (selectedStores.length === 0) {
      return uniqueOptionsMap[COMP_FIELDS2.branchName] || [];
    }

    const branches = new Set(
      details
        .filter((item) =>
          selectedStores.includes(normalize(item[COMP_FIELDS2.storeName])),
        )
        .map((item) => item[COMP_FIELDS2.branchName])
        .filter(Boolean)
        .map(normalize),
    );

    return Array.from(branches);
  }, [checkboxFilters, details, uniqueOptionsMap]);

  const getOptions = (field) => {
    const values =
      field === COMP_FIELDS2.branchName
        ? filteredBranches
        : uniqueOptionsMap[field] || [];

    return values.map((val) => ({
      value: val,
      label: getOptionLabel(field, val),
    }));
  };

  const filteredDetails = useMemo(() => {
    return details.filter((item) => {
      if (searchText.date) {
        const [year, monthNum] = searchText.date.split("-");
        const selectedMonthName = monthNames[monthNum];

        const itemDate = item[COMP_FIELDS2.date] || "";
        const itemMonthRaw = itemDate.split("-")[1];
        const itemYearRaw = itemDate.split("-")[2];

        if (
          !itemMonthRaw ||
          itemMonthRaw !== selectedMonthName ||
          !itemYearRaw ||
          itemYearRaw !== year
        )
          return false;
      }

      for (const field of checkboxFields) {
        const active = checkboxFilters[field];
        if (active?.length) {
          const itemValue = normalize(item[field]);
          const normalizedActive = active.map(normalize);
          if (!normalizedActive.includes(itemValue)) return false;
        }
      }

      return true;
    });
  }, [checkboxFields, checkboxFilters, details, searchText]);

  const summarizedData = useMemo(() => {
    const groupedSummary = {};

    filteredDetails.forEach((item) => {
      const store = item[COMP_FIELDS2.storeName];
      const groupKey = store;

      if (!groupedSummary[groupKey]) {
        groupedSummary[groupKey] = {
          store: store,
          count: 0,
          totalOrder: 0,
          totalEndurance: 0,
          totalRefund: 0,
        };
      }

      groupedSummary[groupKey].count += 1;
      groupedSummary[groupKey].totalOrder += parseFloat(
        item[COMP_FIELDS2.orderAmount] || 0,
      );
      groupedSummary[groupKey].totalEndurance += parseFloat(
        item[COMP_FIELDS2.enduranceAmount] || 0,
      );
      groupedSummary[groupKey].totalRefund += parseFloat(
        item[COMP_FIELDS2.refundAmount] || 0,
      );
    });

    return Object.values(groupedSummary);
  }, [filteredDetails]);

  // Rows per store, built once (each summary row used to re-filter the whole
  // filtered dataset).
  const rowsByStore = useMemo(
    () => groupBy(filteredDetails, (row) => row[COMP_FIELDS2.storeName]),
    [filteredDetails],
  );

  const deleteByIdsName = async () => {
    const token = localStorage.getItem("auth-token");
    const ids = filteredDetails.map((item) => item._id);

    if (!ids.length) return;
    // Handler-level authorization: the bulk-delete permission and every affected
    // restaurant must pass (rows carry the restaurant _id).
    if (!can("report.bulkDelete") || !canForAll("report.entry.delete", filteredDetails.map((item) => item.storeId))) return;
    if (token) {
      const result = await deleteCompByIdsAPI(token, ids);
      if (result) {
        setDeleteTarget(null);
        setDeleteError("");
        fetchDetails();
      } else {
        // Keep the dialog open so the failure is visible and can be retried.
        setDeleteError(text.actionFailed);
      }
    } else {
      handleTokenExpiration();
    }
  };

  // Changing stores clears branch selections that no longer belong to any
  // selected store (they kept filtering while their tags showed no label).
  const handleCheckboxChange = (field, selected) => {
    setCheckboxFilters((prev) => {
      const next = { ...prev, [field]: selected };
      if (field !== COMP_FIELDS2.storeName) return next;
      const selectedStores = next[COMP_FIELDS2.storeName] || [];
      const available = new Set(
        details
          .filter(
            (item) =>
              !selectedStores.length ||
              selectedStores.includes(normalize(item[COMP_FIELDS2.storeName])),
          )
          .map((item) => normalize(item[COMP_FIELDS2.branchName])),
      );
      next[COMP_FIELDS2.branchName] = pruneSelection(prev[COMP_FIELDS2.branchName], [...available]);
      return next;
    });
  };

  const hasActiveFilters =
    Boolean(searchText.date) ||
    Object.values(checkboxFilters).some((values) => values?.length);

  const handleClearFilters = () => {
    setSearchText({ date: "", other: "" });
    setCheckboxFilters({});
  };

  return (
    <div className="report-details-table">
      <div className="report-details-search-panel">
        <div className="report-details-text-filter">
          <Input
            type="month"
            fullWidth={false}
            label={translations[lang].reports.details.inputTitles[0]}
            value={searchText.date || ""}
            onChange={(e) =>
              setSearchText((prev) => ({ ...prev, date: e.target.value }))
            }
          />

          {checkboxFields.map((field, i) => (
            <div
              key={field}
              className={field === COMP_FIELDS2.cause ? "wide" : ""}
            >
              <SearchCheckList
                fullWidth="full-width"
                placeholder={
                  translations[lang].reports.details.inputTitles[i + 1]
                }
                options={getOptions(field)}
                selected={checkboxFilters[field] || []}
                onChange={(selected) => handleCheckboxChange(field, selected)}
              />
            </div>
          ))}
        </div>

        <div className="report-filter-actions">
          <Button
            variant="warning"
            appearance="soft"
            size="sm"
            onClick={handleClearFilters}
          >
            {translations[lang].reports.details.inputTitles[6]}
          </Button>
            <div className="exportToExcel">
              <Button
                className="report-export-btn"
                variant="primary"
                appearance="soft"
                size="sm"
                icon={<CloudDownload />}
                iconPosition="end"
                aria-label={text.exportLabel(filteredDetails.length)}
                title={text.exportLabel(filteredDetails.length)}
                disabled={!filteredDetails.length}
                onClick={() => exportToExcel(filteredDetails, "Details")}
              >
                {text.export}
              </Button>
            </div>

          {can("report.bulkDelete") && (
            <div
              className="delete-by-container"
              style={{ marginInlineStart: "auto" }}
            >
              <Button
                variant="danger"
                appearance=""
                size="sm"
                rounded
                icon={<Trash2 size={18} />}
                title={text.deleteFiltered}
                aria-label={text.deleteFiltered}
                disabled={!filteredDetails.length}
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(true);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="report-details-card-col">
        <div className="report-details-header-card">
          {translations[lang].reports.details.header.map((item, i) => (
            <p key={i}>
              <strong>{item}</strong>
            </p>
          ))}
        </div>
        {loading && (
          <p className="report-state" role="status">
            {text.loading}
          </p>
        )}
        {!loading && summarizedData.length === 0 && (
          <p className="report-state">{text.empty}</p>
        )}
        {summarizedData.map((summary) => {
          // Keyed by store: an index pointed at a different store once the
          // filters changed the list.
          const isExpanded = expandedGroup === summary.store;
          const groupDetails = rowsByStore.get(summary.store) || [];
          const isApproved = groupDetails.find((a) => a.isApproved === false);

          return (
            <React.Fragment key={summary.store}>
              <div
                {...rowToggleProps(isExpanded, () =>
                  setExpandedGroup(isExpanded ? null : summary.store),
                )}
                className={`report-details-summary-card ${isExpanded ? "colored" : ""} ${isApproved && !isApproved?.isApproved ? "notApproved" : ""}`}
              >
                <div className="report-details-p center">
                  <p>
                    <strong>{summary.store}</strong>
                  </p>
                  {isApproved && (
                    <span className="report-pending-tag">{text.pendingApproval}</span>
                  )}
                </div>
                <div className="report-details-p">
                  <p>
                    <strong>{summary.count}</strong>
                  </p>
                </div>
                <div className="report-details-p">
                  <p>
                    <strong>
                      {formatter.format(summary.totalOrder.toFixed(2))}
                    </strong>
                  </p>
                </div>
                <div className="report-details-p">
                  <p>
                    <strong>
                      {formatter.format(summary.totalEndurance.toFixed(2))}
                    </strong>
                  </p>
                </div>
                <div className="report-details-p">
                  <p>
                    <strong>
                      {formatter.format(summary.totalRefund.toFixed(2))}
                    </strong>
                  </p>
                </div>
                <div className="report-expand-icon">
                  {isExpanded ? <ArrowUp size={18} /> : <ArrowDown size={18} />}
                </div>
              </div>
              {isExpanded && (
                <DetailsExpand
                  data={groupDetails}
                  onRefresh={fetchDetails}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {deleteTarget && (
        <DeleteModal
          cancel={() => {
            setDeleteTarget(null);
            setDeleteError("");
          }}
          id={deleteTarget}
          handleDelete={deleteByIdsName}
          error={deleteError}
          description={`${text.deleteFilteredDescription(filteredDetails.length)}${
            hasActiveFilters ? "" : ` ${text.deleteAllWarning}`
          }`}
        />
      )}
    </div>
  );
};

// PAGED_REPORTS on: server-side filtering, sorting and paging; off (default): the table below, unchanged.
export const Details = ({ active = true }) =>
  isFeatureEnabled(FEATURE_NAMES.PAGED_REPORTS) ? <DetailsPaged active={active} /> : <DetailsUnpaged />;
